import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Collapse, IconButton, Chip,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, fadeInUp } from '../results/ResultsDesignTokens';

const AGENT_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'battery', label: 'Battery-only' },
    { id: 'batt_ev', label: 'Batt+EV' },
    { id: 'batt_cold', label: 'Batt+Cold' },
    { id: '3agent', label: '3-agent' },
];

const cellSx = {
    fontFamily: vortxTypography.mono,
    fontSize: '0.7rem',
    color: COLORS.text.primary,
    borderBottom: `1px solid ${alpha(COLORS.accent, 0.08)}`,
    py: 1,
    px: 1.5,
};

const headerSx = {
    ...cellSx,
    color: COLORS.text.muted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontSize: '0.6rem',
};

function getPopColor(pop) {
    if (pop == null) return COLORS.text.muted;
    if (pop >= 90) return COLORS.efficiency;
    if (pop >= 70) return COLORS.solar;
    return COLORS.loss;
}

function detectAgentCombo(analysis) {
    const id = (analysis.scenario_id || '').toLowerCase();
    const decomp = analysis.peak_decomposition || {};
    const agents = Object.keys(decomp);

    const hasEv = agents.some(a => a.includes('ev')) || id.includes('ev');
    const hasCold = agents.some(a => a.includes('cold')) || id.includes('cold');
    const hasBattery = agents.some(a => a.includes('batt')) || id.includes('batt');

    if (hasEv && hasCold) return '3agent';
    if (hasEv && hasBattery) return 'batt_ev';
    if (hasCold && hasBattery) return 'batt_cold';
    return 'battery';
}

function PeakDecomposition({ decomposition }) {
    if (!decomposition || Object.keys(decomposition).length === 0) {
        return <Typography sx={{ ...cellSx, border: 'none', p: 0 }}>--</Typography>;
    }

    const total = Object.values(decomposition).reduce((s, v) => s + Math.abs(v), 0);
    if (total === 0) return <Typography sx={{ ...cellSx, border: 'none', p: 0 }}>--</Typography>;

    const agentColors = {
        battery: COLORS.bess,
        ev: COLORS.ev,
        cold: COLORS.grid,
    };

    return (
        <Box sx={{ display: 'flex', height: 14, borderRadius: 2, overflow: 'hidden', minWidth: 100 }}>
            {Object.entries(decomposition).map(([agent, power]) => {
                const pct = (Math.abs(power) / total) * 100;
                const colorKey = Object.keys(agentColors).find(k => agent.toLowerCase().includes(k));
                const color = colorKey ? agentColors[colorKey] : COLORS.text.muted;
                return (
                    <Box
                        key={agent}
                        title={`${agent}: ${power.toFixed(0)} kW`}
                        sx={{
                            width: `${pct}%`,
                            bgcolor: alpha(color, 0.7),
                            minWidth: pct > 0 ? 2 : 0,
                        }}
                    />
                );
            })}
        </Box>
    );
}

function AnalysisRow({ analysis }) {
    const [open, setOpen] = useState(false);

    const popColor = getPopColor(analysis.cost_pop_pct);
    const rootCause = analysis.qualitative_analysis?.root_cause;

    return (
        <>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(COLORS.accent, 0.04) },
                }}
            >
                <TableCell sx={cellSx} width={40}>
                    <IconButton size="small" sx={{ color: COLORS.text.secondary, p: 0 }}>
                        {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ ...cellSx, color: COLORS.accent }}>
                    {(analysis.scenario_id || '').length > 20
                        ? `${analysis.scenario_id.slice(0, 20)}...`
                        : analysis.scenario_id || '--'}
                </TableCell>
                <TableCell sx={{ ...cellSx, color: popColor, fontWeight: 700, textAlign: 'right' }}>
                    {analysis.cost_pop_pct != null ? `${analysis.cost_pop_pct.toFixed(1)}%` : '--'}
                </TableCell>
                <TableCell sx={cellSx}>
                    <PeakDecomposition decomposition={analysis.peak_decomposition} />
                </TableCell>
                <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: rootCause ? COLORS.text.primary : COLORS.text.muted,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {rootCause || 'N/A'}
                    </Typography>
                </TableCell>
                <TableCell sx={{ ...cellSx, textAlign: 'right' }}>
                    {analysis.agent_coincidence_score != null
                        ? analysis.agent_coincidence_score.toFixed(2)
                        : '--'}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? `1px solid ${alpha(COLORS.accent, 0.08)}` : 'none' }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <DetailBlock label="Energy / Demand Split" data={analysis.energy_demand_split} />
                            <DetailBlock label="Peak Decomposition" data={analysis.peak_decomposition} />
                            {analysis.worst_steps && analysis.worst_steps.length > 0 && (
                                <Box sx={{ flex: '1 1 300px' }}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        color: COLORS.text.muted,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        mb: 0.5,
                                    }}>
                                        Worst Steps
                                    </Typography>
                                    <Box component="pre" sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        color: COLORS.text.primary,
                                        bgcolor: alpha(COLORS.accent, 0.04),
                                        p: 1.5,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        m: 0,
                                        maxHeight: 120,
                                    }}>
                                        {JSON.stringify(analysis.worst_steps, null, 2)}
                                    </Box>
                                </Box>
                            )}
                            {analysis.qualitative_analysis && (
                                <Box sx={{ flex: '1 1 300px' }}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        color: COLORS.text.muted,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        mb: 0.5,
                                    }}>
                                        Qualitative Analysis
                                    </Typography>
                                    <Box component="pre" sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        color: COLORS.text.primary,
                                        bgcolor: alpha(COLORS.accent, 0.04),
                                        p: 1.5,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        m: 0,
                                        maxHeight: 120,
                                    }}>
                                        {JSON.stringify(analysis.qualitative_analysis, null, 2)}
                                    </Box>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <MetricChip label="Peak PoP" value={analysis.peak_pop_pct} suffix="%" />
                                <MetricChip label="LP/MILP Gap" value={analysis.lp_milp_gap_pct} suffix="%" />
                                <MetricChip label="EV Clustering" value={analysis.ev_charge_clustering} />
                                <MetricChip label="Batt SOC @ Peak" value={analysis.battery_soc_at_peak} suffix="%" />
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

function DetailBlock({ label, data }) {
    if (!data || Object.keys(data).length === 0) return null;

    return (
        <Box sx={{ flex: '1 1 200px' }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.6rem',
                color: COLORS.text.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 0.5,
            }}>
                {label}
            </Typography>
            {Object.entries(data).map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.65rem', color: COLORS.text.secondary }}>
                        {k}
                    </Typography>
                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.65rem', color: COLORS.text.primary, fontWeight: 600 }}>
                        {typeof v === 'number' ? v.toFixed(1) : String(v)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function MetricChip({ label, value, suffix = '' }) {
    if (value == null) return null;
    const display = typeof value === 'number' ? `${value.toFixed(1)}${suffix}` : String(value);
    return (
        <Box sx={{
            px: 1.5,
            py: 0.5,
            bgcolor: alpha(COLORS.accent, 0.08),
            borderRadius: 1,
            border: `1px solid ${alpha(COLORS.accent, 0.12)}`,
        }}>
            <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.55rem', color: COLORS.text.muted }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.7rem', color: COLORS.text.primary, fontWeight: 600 }}>
                {display}
            </Typography>
        </Box>
    );
}

export default function AnalysisTab({ analyses }) {
    const [filter, setFilter] = useState('all');

    const filtered = useMemo(() => {
        if (filter === 'all') return analyses;
        return analyses.filter(a => detectAgentCombo(a) === filter);
    }, [analyses, filter]);

    if (!analyses || analyses.length === 0) {
        return (
            <Box sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: COLORS.bg.card,
                border: `1px solid ${alpha(COLORS.accent, 0.1)}`,
                borderRadius: 2,
                animation: `${fadeInUp} 0.4s ease-out`,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.8rem',
                    color: COLORS.text.muted,
                }}>
                    No analyses yet. Run benchmarks with the post-run analyst enabled.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ animation: `${fadeInUp} 0.4s ease-out` }}>
            {/* Filter chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {AGENT_FILTERS.map(f => (
                    <Chip
                        key={f.id}
                        label={f.label}
                        size="small"
                        onClick={() => setFilter(f.id)}
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            fontWeight: filter === f.id ? 600 : 400,
                            color: filter === f.id ? COLORS.accent : COLORS.text.secondary,
                            bgcolor: filter === f.id ? alpha(COLORS.accent, 0.12) : 'transparent',
                            border: `1px solid ${filter === f.id ? alpha(COLORS.accent, 0.4) : alpha(COLORS.accent, 0.12)}`,
                            '&:hover': {
                                bgcolor: alpha(COLORS.accent, 0.08),
                            },
                        }}
                    />
                ))}
            </Box>

            <TableContainer sx={{
                bgcolor: COLORS.bg.card,
                border: `1px solid ${alpha(COLORS.accent, 0.1)}`,
                borderRadius: 2,
            }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerSx} width={40} />
                            <TableCell sx={headerSx}>Scenario ID</TableCell>
                            <TableCell sx={{ ...headerSx, textAlign: 'right' }}>PoP%</TableCell>
                            <TableCell sx={headerSx}>Peak Decomposition</TableCell>
                            <TableCell sx={headerSx}>Root Cause</TableCell>
                            <TableCell sx={{ ...headerSx, textAlign: 'right' }}>Coincidence</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map((a, i) => (
                            <AnalysisRow key={a.scenario_id || i} analysis={a} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
